// CRM BL: Super Admin › Settings › AI Assist, seção "Classificador (TypeSafe Jev)".
// Provedor TypeSafe direto ou OpenRouter; "Carregar modelos" (no OpenRouter, só os
// modelos Jev) e "Testar classificador" com o resultado logo abaixo do botão.
// A chave digitada só vai para o próprio servidor (rotas do Super Admin).

import { createModelPicker } from './modelPicker';

const RESULT_CLASSES = {
  success: ['outline-n-teal-6', 'bg-n-teal-2', 'text-n-teal-11'],
  error: ['outline-n-ruby-6', 'bg-n-ruby-2', 'text-n-ruby-11'],
  info: ['outline-n-weak', 'bg-n-alpha-1', 'text-n-slate-11'],
};

const parseJSON = value => {
  try {
    return JSON.parse(value || 'null');
  } catch (error) {
    return null;
  }
};

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

const setResult = (element, type, lines) => {
  Object.values(RESULT_CLASSES)
    .flat()
    .forEach(name => element.classList.remove(name));
  element.classList.add(...RESULT_CLASSES[type]);
  element.replaceChildren(
    ...lines.map((line, index) => {
      const row = document.createElement('div');
      row.className =
        index === 0 ? 'font-medium' : 'pt-0.5 text-xs opacity-90 break-words';
      row.textContent = line;
      return row;
    })
  );
  element.classList.remove('hidden');
};

const describeError = data => {
  const status = data?.status ? `Erro ${data.status}` : 'Erro';
  return `${status}: ${data?.error || 'o classificador não respondeu como esperado.'}`;
};

const initializeClassifierForm = () => {
  const section = document.querySelector('[data-classifier-form]');
  if (!section) return;

  const providers = parseJSON(section.dataset.providers) || [];
  const saved = parseJSON(section.dataset.saved) || {};
  const el = selector => section.querySelector(selector);
  const providerSelect = el('[data-classifier-provider]');
  const keyInput = el('[data-classifier-key]');
  const modelInput = el('[data-classifier-model]');
  const modelsStatus = el('[data-classifier-models-status]');
  const loadButton = el('[data-classifier-load-models]');
  const testButton = el('[data-classifier-test]');
  const testResult = el('[data-classifier-test-result]');
  const sharedHint = el('[data-classifier-shared-hint]');
  const modelPicker = createModelPicker(modelInput);

  const preset = () =>
    providers.find(item => item.key === providerSelect.value);
  const usesSharedKey = () =>
    providerSelect.value === 'openrouter' && saved.shared_openrouter_key;
  const hasUsableKey = () =>
    keyInput.value.trim() ||
    (saved.has_key && saved.provider === providerSelect.value) ||
    usesSharedKey();

  const setStatus = (text, tone = 'muted') => {
    modelsStatus.textContent = text;
    modelsStatus.classList.toggle('text-n-ruby-11', tone === 'error');
    modelsStatus.classList.toggle('text-n-teal-11', tone === 'success');
    modelsStatus.classList.toggle('text-n-slate-11', tone === 'muted');
  };

  const render = () => {
    modelPicker.setDefault(preset()?.model || 'jev-1.13.0');
    sharedHint.classList.toggle(
      'hidden',
      !(usesSharedKey() && !(saved.has_key && saved.provider === 'openrouter'))
    );
    if (saved.has_key && saved.provider === providerSelect.value) {
      keyInput.placeholder = keyInput.dataset.savedPlaceholder;
    } else {
      keyInput.placeholder =
        providerSelect.value === 'openrouter'
          ? 'Cole a chave do OpenRouter (sk-or-...)'
          : 'Cole a chave da TypeSafe';
    }
  };

  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({
      error: `o servidor respondeu ${response.status}.`,
    }));
    return { ok: response.ok, data };
  };

  const requestBody = () => ({
    provider: providerSelect.value,
    api_key: keyInput.value.trim(),
    model: modelInput.value.trim(),
  });

  const loadModels = async () => {
    if (!hasUsableKey()) {
      setStatus('Cole a chave para carregar os modelos.');
      return;
    }
    loadButton.disabled = true;
    modelPicker.setLoading(true);
    setStatus('Carregando modelos…');
    try {
      const { ok, data } = await post(section.dataset.modelsUrl, requestBody());
      if (!ok) {
        setStatus(describeError(data), 'error');
        return;
      }
      const models = data.models || [];
      modelPicker.setModels(models);
      setStatus(
        `${models.length} modelo(s) carregado(s). Abra a lista para escolher.`,
        'success'
      );
    } catch (error) {
      setStatus('Não foi possível falar com o servidor.', 'error');
    } finally {
      loadButton.disabled = false;
      modelPicker.setLoading(false);
    }
  };

  const testClassifier = async () => {
    if (!hasUsableKey()) {
      setResult(testResult, 'error', [
        'Erro: cole a chave do classificador antes de testar.',
      ]);
      return;
    }
    testButton.disabled = true;
    setResult(testResult, 'info', ['Testando classificador…']);
    try {
      const { data } = await post(section.dataset.testUrl, requestBody());
      if (!data.ok) {
        setResult(testResult, 'error', [describeError(data)]);
        return;
      }
      const percent = Math.round((data.yes_probability || 0) * 100);
      setResult(testResult, 'success', [
        `Classificador ok: ${data.model} respondeu em ${data.latency_ms} ms.`,
        `Pergunta de teste "o cliente pede um humano?": sim com ${percent}% de probabilidade.`,
        'Salve para usar estas configurações.',
      ]);
    } catch (error) {
      setResult(testResult, 'error', [
        'Erro: não foi possível falar com o servidor.',
      ]);
    } finally {
      testButton.disabled = false;
    }
  };

  providerSelect.addEventListener('change', () => {
    modelPicker.setModels([]);
    modelPicker.setValue('');
    testResult.classList.add('hidden');
    setStatus('');
    render();
  });
  loadButton.addEventListener('click', loadModels);
  testButton.addEventListener('click', testClassifier);
  render();
};

export default initializeClassifierForm;
